/**
 * The Live tab's real data: every WhatsApp chat this login may see (head office
 * sees all, by the row rules in migration 20261014b), refreshed live as messages
 * arrive. Actions go through the messaging-send edge function, because logins
 * can only read these tables.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../../lib/supabase';

export type Brand = 'cmb' | 'pos';
export type Stage = 'unassigned' | 'waiting_operator' | 'waiting_customer' | 'booked' | 'closed';
export type Inbox = 'leads' | 'billing' | 'customer_service';
/**
 * The board's groups, the same words as CMB Chats (Gary, 16 Oct). Needs reply: we owe the reply.
 * Open: the customer wrote in the last 24 hours, nothing owed. Unpaid: an invoice is still owing.
 * Stale: 24 hours up, nothing owed. Archived: quiet 7 days, nothing owed (off the board).
 */
export type Group = 'unassigned' | 'needs_reply' | 'open' | 'unpaid' | 'stale' | 'archived' | 'closed';

/** The oldest invoice still owing for the chat's customer. */
export interface Owing { invoiceNumber: string; balance: number; sentAt: string }

export interface LiveChat {
  id: string;
  brand: Brand;
  name: string;
  phone: string | null;
  area: string;
  owner: string;
  assignedUserId: string | null;
  inbox: Inbox;
  stage: Stage;
  updatedAt: string;
  last: string;
  source: string;
  /** Minutes left before the reply deadline; negative once it has passed. Null when none is running. */
  dueMinutes: number | null;
  escalated: boolean;
  /** More than 24 hours since the customer last wrote: approved messages only. */
  windowClosed: boolean;
  leadId: string | null;
  closedReason: string | null;
  /** Dr C on this chat (Phase 2A): off, ai (answering), human (a person has it), human_required (handed over). */
  aiState: 'off' | 'ai' | 'human' | 'human_required';
  aiHandover: string | null;
  aiSummary: string | null;
  group: Group;
  booked: boolean;
  /** Set only when the customer has an invoice sent or overdue with money owing. */
  owing: Owing | null;
  /** The last message either way, for archiving after 7 quiet days. */
  lastActivityAt: string;
}

export interface LiveMessage {
  id: string;
  direction: 'in' | 'out' | 'internal';
  sender: 'customer' | 'operator' | 'auto' | 'system' | 'note' | 'assistant';
  kind: string;
  body: string | null;
  mediaPath: string | null;
  mediaMime: string | null;
  signOff: string | null;
  reaction: string | null;
  deliveryStatus: string;
  /** Meta's reason when WhatsApp could not deliver it. */
  error: string | null;
  createdAt: string;
}

export const IN_PLAY: Stage[] = ['unassigned', 'waiting_operator', 'waiting_customer'];

export const GROUPS: { id: Group; label: string; color: string }[] = [
  { id: 'unassigned', label: 'Unassigned', color: '#dc2626' },
  { id: 'needs_reply', label: 'Needs reply', color: '#f59e0b' },
  { id: 'open', label: 'Open', color: '#16a34a' },
  { id: 'unpaid', label: 'Unpaid', color: '#6366f1' },
  { id: 'stale', label: 'Stale', color: '#94a3b8' },
];
export const GROUP_LABEL: Record<Group, string> = {
  unassigned: 'Unassigned', needs_reply: 'Needs reply', open: 'Open', unpaid: 'Unpaid', stale: 'Stale', archived: 'Archived', closed: 'Closed',
};
const ARCHIVE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/** On the board: every group but Archived and Closed. */
export function onBoard(c: LiveChat): boolean {
  return c.group !== 'archived' && c.group !== 'closed';
}

export function groupOf(o: { stage: Stage; windowClosed: boolean; owing: Owing | null; lastActivityAt: string }, now: number): Group {
  if (o.stage === 'closed') return 'closed';
  if (o.stage === 'unassigned') return 'unassigned';
  if (o.stage === 'waiting_operator') return 'needs_reply';
  if (o.owing) return 'unpaid';
  if (now - new Date(o.lastActivityAt).getTime() > ARCHIVE_AFTER_MS) return 'archived';
  return o.windowClosed ? 'stale' : 'open';
}

/** Order within a column: what needs doing first at the top. */
export function boardOrder(group: Group, a: LiveChat, b: LiveChat): number {
  const t = (iso: string) => new Date(iso).getTime();
  if (group === 'unassigned') return t(a.lastActivityAt) - t(b.lastActivityAt);
  if (group === 'needs_reply') return (a.dueMinutes ?? Infinity) - (b.dueMinutes ?? Infinity);
  if (group === 'unpaid') return t(a.owing?.sentAt ?? a.lastActivityAt) - t(b.owing?.sentAt ?? b.lastActivityAt);
  return t(b.lastActivityAt) - t(a.lastActivityAt);
}

export const STAGES: { id: Stage; label: string; color: string }[] = [
  { id: 'unassigned', label: 'Unassigned', color: '#dc2626' },
  { id: 'waiting_operator', label: 'Waiting on operator', color: '#f59e0b' },
  { id: 'waiting_customer', label: 'Waiting on customer', color: '#6366f1' },
  { id: 'booked', label: 'Booked', color: '#16a34a' },
  { id: 'closed', label: 'Closed', color: '#94a3b8' },
];

export const INBOX_LABEL: Record<Inbox, string> = {
  leads: 'Leads',
  billing: 'Accounts',
  customer_service: 'Customer service',
};

const DAY_MS = 24 * 60 * 60 * 1000;

interface Row {
  id: string;
  brand: Brand;
  inbox: Inbox;
  status: Stage;
  assigned_user_id: string | null;
  lead_id: string | null;
  qualifier: Record<string, string> | null;
  source: Record<string, unknown> | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  customer_id: string | null;
  last_message_preview: string | null;
  reply_due_at: string | null;
  escalated_at: string | null;
  closed_reason: string | null;
  updated_at: string;
  ai_state: LiveChat['aiState'];
  ai_handover_reason: string | null;
  ai_summary: string | null;
  contact: { display_name: string | null; phone_e164: string | null } | null;
  territory: { name: string } | null;
  assignee: { first_name: string | null; last_name: string | null } | null;
  customer: { first_name: string | null; last_name: string | null } | null;
}

function sourceLabel(source: Record<string, unknown> | null): string {
  const headline = typeof source?.headline === 'string' ? source.headline : '';
  if (headline) return `Ad: ${headline}`;
  if (source?.source_type === 'ad') return 'Facebook or Instagram ad';
  if (source?.source_type === 'post') return 'Facebook or Instagram post';
  // A pre-filled link on our Google, Facebook or Instagram profile (17 Oct 2026).
  if (source?.channel === 'google') return 'Found us on Google';
  if (source?.channel === 'facebook') return 'Found us on Facebook';
  if (source?.channel === 'instagram') return 'Found us on Instagram';
  return 'WhatsApp';
}

function toChat(r: Row, now: number, owingBy: Map<string, Owing>): LiveChat {
  const assignee = r.assignee ? [r.assignee.first_name, r.assignee.last_name].filter(Boolean).join(' ') : '';
  const customerName = r.customer ? [r.customer.first_name, r.customer.last_name].map((p) => p?.trim()).filter(Boolean).join(' ') : '';
  const windowClosed = !r.last_inbound_at || now - new Date(r.last_inbound_at).getTime() > DAY_MS;
  const owing = (r.customer_id && owingBy.get(r.customer_id)) || null;
  const lastActivityAt = [r.last_inbound_at, r.last_outbound_at].filter((x): x is string => !!x).sort().pop() ?? r.updated_at;
  const owner = assignee || (r.inbox === 'leads' ? 'Unassigned' : INBOX_LABEL[r.inbox]);
  const running = IN_PLAY.includes(r.status) && r.status !== 'waiting_customer' && !!r.reply_due_at;
  return {
    id: r.id,
    brand: r.brand,
    // The Sentinel customer's name first, so an Accounts chat is not just a number (Gary, 16 Oct).
    name: customerName || r.contact?.display_name?.trim() || r.contact?.phone_e164 || 'Unknown',
    phone: r.contact?.phone_e164 ?? null,
    // A suburb only matters on a lead; an invoice chat shows none (Gary, 16 Oct).
    area: r.qualifier?.suburb || r.territory?.name || (r.inbox === 'leads' ? 'Suburb not given yet' : ''),
    owner,
    assignedUserId: r.assigned_user_id,
    inbox: r.inbox,
    stage: r.status,
    updatedAt: r.updated_at,
    last: r.last_message_preview ?? '',
    source: sourceLabel(r.source),
    dueMinutes: running ? Math.ceil((new Date(r.reply_due_at as string).getTime() - now) / 60_000) : null,
    escalated: !!r.escalated_at,
    windowClosed,
    leadId: r.lead_id,
    closedReason: r.closed_reason,
    aiState: r.ai_state ?? 'off',
    aiHandover: r.ai_handover_reason,
    aiSummary: r.ai_summary,
    group: groupOf({ stage: r.status, windowClosed, owing, lastActivityAt }, now),
    booked: r.status === 'booked',
    owing,
    lastActivityAt,
  };
}

const CHAT_FIELDS = `id, brand, inbox, status, assigned_user_id, lead_id, customer_id, qualifier, source, last_inbound_at, last_outbound_at,
  last_message_preview, reply_due_at, escalated_at, closed_reason, updated_at, ai_state, ai_handover_reason, ai_summary,
  contact:contact_identities(display_name, phone_e164),
  territory:territories(name),
  assignee:users!conversations_assigned_user_id_fkey(first_name, last_name),
  customer:customers!conversations_customer_id_fkey(first_name, last_name)`;

/** Every chat, newest first, reloaded on any change and once a minute so the deadlines count down. */
export function useLiveChats() {
  const [chats, setChats] = useState<LiveChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('conversations').select(CHAT_FIELDS)
      .order('updated_at', { ascending: false }).limit(500);
    if (e) { setError(e.message); setLoading(false); return; }
    const rows = (data ?? []) as unknown as Row[];
    // Invoices still owing, by customer: the oldest one is what the Unpaid card shows.
    const customerIds = [...new Set(rows.map((r) => r.customer_id).filter((x): x is string => !!x))];
    const owingBy = new Map<string, Owing>();
    if (customerIds.length) {
      const { data: inv } = await supabase.from('invoices').select('customer_id, invoice_number, balance_due, sent_at, created_at')
        .in('customer_id', customerIds).in('status', ['sent', 'overdue']).gt('balance_due', 0).order('created_at', { ascending: true });
      for (const i of (inv ?? []) as { customer_id: string; invoice_number: string; balance_due: number | string; sent_at: string | null; created_at: string }[]) {
        if (!owingBy.has(i.customer_id)) owingBy.set(i.customer_id, { invoiceNumber: i.invoice_number, balance: Number(i.balance_due), sentAt: i.sent_at ?? i.created_at });
      }
    }
    const now = Date.now();
    setChats(rows.map((r) => toChat(r, now, owingBy)));
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    // A burst of messages reloads once.
    const soon = () => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), 400);
    };
    const channel = supabase.channel('whatsapp-live-chats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, soon)
      .subscribe();
    const tick = window.setInterval(() => void load(), 60_000);
    return () => {
      void supabase.removeChannel(channel);
      window.clearInterval(tick);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [load]);

  return { chats, loading, error, reload: load };
}

interface MessageRow {
  id: string;
  direction: LiveMessage['direction'];
  sender: LiveMessage['sender'];
  kind: string;
  body: string | null;
  media_path: string | null;
  media_mime: string | null;
  sign_off: string | null;
  reaction: string | null;
  delivery_status: string;
  error: string | null;
  created_at: string;
}

/** A divider line in the thread, shown with the existing system-line style. */
function dividerRow(id: string, text: string, at: string): MessageRow {
  return {
    id, direction: 'internal', sender: 'system', kind: 'system', body: text, media_path: null, media_mime: null,
    sign_off: null, reaction: null, delivery_status: 'delivered', error: null, created_at: at,
  };
}

/** One chat's messages, live. */
export function useChatMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<LiveMessage[]>([]);

  const load = useCallback(async () => {
    if (!conversationId) { setMessages([]); return; }
    // The customer's earlier chats (same contact, same brand) are shown above this one, so it
    // reads as one continuous WhatsApp thread (Gary, 14 Oct).
    const { data: conv } = await supabase.from('conversations').select('identity_id, brand').eq('id', conversationId).maybeSingle();
    const { data: siblings } = conv
      ? await supabase.from('conversations').select('id').eq('identity_id', conv.identity_id).eq('brand', conv.brand)
      : { data: [] as { id: string }[] };
    const ids = [...new Set([conversationId, ...((siblings ?? []) as { id: string }[]).map((c) => c.id)])];
    const { data } = await supabase.from('conversation_messages')
      .select('id, conversation_id, direction, sender, kind, body, media_path, media_mime, sign_off, reaction, delivery_status, error, created_at')
      .in('conversation_id', ids).order('created_at', { ascending: true }).limit(1000);
    const all = (data ?? []) as (MessageRow & { conversation_id: string })[];
    const own = all.filter((m) => m.conversation_id === conversationId);
    const start = own[0]?.created_at;
    const earlier = start ? all.filter((m) => m.conversation_id !== conversationId && m.created_at < start) : [];
    const rows: MessageRow[] = [];
    let current = '';
    for (const m of earlier) {
      if (m.conversation_id !== current) { rows.push(dividerRow(`earlier-${m.conversation_id}`, 'Earlier chat', m.created_at)); current = m.conversation_id; }
      rows.push(m);
    }
    if (earlier.length && start) rows.push(dividerRow(`current-${conversationId}`, 'This chat', start));
    rows.push(...own);
    setMessages(rows.map((m) => ({
      id: m.id,
      direction: m.direction,
      sender: m.sender,
      kind: m.kind,
      body: m.body,
      mediaPath: m.media_path,
      mediaMime: m.media_mime,
      signOff: m.sign_off,
      reaction: m.reaction,
      deliveryStatus: m.delivery_status,
      error: m.error,
      createdAt: m.created_at,
    })));
  }, [conversationId]);

  useEffect(() => {
    void load();
    if (!conversationId) return;
    const channel = supabase.channel(`whatsapp-chat-${conversationId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'conversation_messages', filter: `conversation_id=eq.${conversationId}`,
      }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [conversationId, load]);

  return messages;
}

/** A short-lived link to a photo or voice note in the private media bucket. */
export async function mediaUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from('conversation-media').createSignedUrl(path, 300);
  return data?.signedUrl ?? null;
}

export type ChatAction =
  | { action: 'take_over' }
  | { action: 'reassign'; user_id: string }
  | { action: 'close'; outcome: 'booked' | 'closed'; reason?: string }
  | { action: 'send'; text: string; sign_with_name?: boolean }
  | { action: 'return_to_ai' }
  | { action: 'ai_off' };

/**
 * Approved messages are stored with their buttons as a last line, "[Pay securely: https://...]
 * [I have a question] [I've already paid]". The customer sees buttons, so the panel shows the
 * text without that line and the buttons by name only (same rule as CMB Chats).
 */
export function splitButtons(body: string): { text: string; buttons: string[] } {
  const lines = body.replace(/\s+$/, '').split('\n');
  const last = lines[lines.length - 1] ?? '';
  if (!/^(\[[^\]]+\]\s*)+$/.test(last.trim())) return { text: body, buttons: [] };
  const buttons = [...last.matchAll(/\[([^\]]+)\]/g)].map((b) => (b[1] ?? '').split(':')[0]!.trim()).filter(Boolean);
  return { text: lines.slice(0, -1).join('\n').replace(/\s+$/, ''), buttons };
}

/** Runs an action on a chat. Returns null when it worked, otherwise the message to show. */
export async function chatAction(conversationId: string, body: ChatAction): Promise<string | null> {
  return (await callMessagingFunction('messaging-send', { conversation_id: conversationId, ...body })).error;
}

/**
 * Calls one of the messaging edge functions. Returns its JSON on success, or the
 * function's own error wording to show (never the transport error).
 */
export async function callMessagingFunction(
  name: 'messaging-send' | 'messaging-templates',
  body: Record<string, unknown>,
): Promise<{ data: Record<string, unknown> | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return { data: (data as Record<string, unknown> | null) ?? null, error: null };
  return { data: null, error: await functionErrorText(error) };
}

async function functionErrorText(error: unknown): Promise<string> {
  // The function answers with { error: '...' }; show that wording rather than the transport error.
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === 'function') {
    try {
      const json = await context.json() as { error?: string };
      if (json.error) return json.error;
    } catch { /* not JSON: fall through */ }
  }
  return 'Something went wrong. Try again.';
}

export interface Operator { id: string; name: string; posStaff: boolean }

/** Active Portal operators (the same test as is_portal_user), for the reassign picker. */
export async function loadOperators(): Promise<Operator[]> {
  const { data } = await supabase.from('users')
    .select('id, first_name, last_name, permissions, is_pos_staff').eq('status', 'active').order('first_name');
  return ((data ?? []) as { id: string; first_name: string | null; last_name: string | null; permissions: unknown; is_pos_staff: boolean | null }[])
    .filter((u) => u.permissions && typeof u.permissions === 'object' && !Array.isArray(u.permissions)
      && Object.keys(u.permissions).some((k) => !['ambassador_portal', 'pos_portal', '_scope'].includes(k)))
    .map((u) => ({ id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unnamed', posStaff: u.is_pos_staff === true }));
}
